import React, { useEffect, useRef, useState } from "react";
import { faBars, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";

const assetBase = "/assets/images/marketing";

const navLinks = [
  { label: "Getting Started", to: "/getting-started" },
  { label: "Pricing", to: "/pricing" },
  { label: "Contact Us", to: "/contact" },
];

const MarketingHeader = () => {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;

    if (mobileNavOpen) {
      document.body.style.overflow = "hidden";
      closeButtonRef.current?.focus();
    }

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [mobileNavOpen]);

  useEffect(() => {
    if (!mobileNavOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setMobileNavOpen(false);
        menuButtonRef.current?.focus();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusableElements = panelRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled])',
      );

      if (!focusableElements?.length) {
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }

      if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [mobileNavOpen]);

  const closeMobileNav = () => {
    setMobileNavOpen(false);
    menuButtonRef.current?.focus();
  };

  return (
    <header className="marketing__header">
      <div className="marketing__container marketing__topbar">
        <Link to="/" aria-label="Insite home">
          <img
            src={`${assetBase}/insite-logo.png`}
            alt="Insite Work"
            className="marketing__brand-logo marketing__brand-logo--invert"
          />
        </Link>

        <nav className="marketing__desktop-nav" aria-label="Primary">
          {navLinks.map((link) => (
            <Link key={link.label} to={link.to}>
              {link.label}
            </Link>
          ))}
          <Link
            to="/pricing"
            className="marketing__button marketing__button--header-accent"
          >
            Start Free Trial
          </Link>
          <Link
            to="/auth/login"
            className="marketing__button marketing__button--header-outline"
          >
            Login
          </Link>
        </nav>

        <button
          ref={menuButtonRef}
          className="marketing__mobile-menu"
          aria-label="Open menu"
          aria-expanded={mobileNavOpen}
          aria-controls="marketing-mobile-nav"
          type="button"
          onClick={() => setMobileNavOpen((current) => !current)}
        >
          <FontAwesomeIcon icon={faBars} />
        </button>
      </div>

      {mobileNavOpen && (
        <>
          <button
            type="button"
            aria-label="Close menu"
            className="marketing__mobile-overlay marketing__mobile-overlay--open"
            onClick={closeMobileNav}
          />

          <aside
            ref={panelRef}
            id="marketing-mobile-nav"
            className="marketing__mobile-panel marketing__mobile-panel--open"
            aria-label="Mobile navigation"
          >
            <div className="marketing__mobile-panel-header">
              <Link
                to="/"
                aria-label="Insite home"
                className="marketing__mobile-panel-brand"
                onClick={closeMobileNav}
              >
                <img
                  src={`${assetBase}/insite-logo.png`}
                  alt="Insite Work"
                  className="marketing__brand-logo marketing__brand-logo--invert"
                />
              </Link>
              <button
                ref={closeButtonRef}
                type="button"
                aria-label="Close menu"
                className="marketing__mobile-panel-close"
                onClick={closeMobileNav}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <nav className="marketing__mobile-panel-inner" aria-label="Mobile">
              {navLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.to}
                  onClick={closeMobileNav}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/pricing"
                className="marketing__button marketing__button--header-accent"
                onClick={closeMobileNav}
              >
                Start Free Trial
              </Link>
              <Link
                to="/auth/login"
                className="marketing__button marketing__button--header-outline"
                onClick={closeMobileNav}
              >
                Login
              </Link>
            </nav>
          </aside>
        </>
      )}
    </header>
  );
};

export default MarketingHeader;
